// frontend/src/pages/Customers/EditCustomer.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { getCustomerById, updateCustomer } from '../../services/api';

const EditCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    customer_code: '',
    email: '',
    phone: '',
    company_name: '',
    address: '',
    customer_type: 'individual',
    is_active: true,
    
    // Credit Information
    credit_limit: '0',
    outstanding_balance: '0',
    payment_terms: '30',
    is_credit_customer: false
  });

  useEffect(() => {
    if (id) {
      fetchCustomer();
    }
  }, [id]);

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await getCustomerById(id);
      if (response.success) {
        const customer = response.data;
        setFormData({
          name: customer.name || '',
          customer_code: customer.customer_code || '',
          email: customer.email || '',
          phone: customer.phone || '',
          company_name: customer.company_name || '',
          address: customer.address || '',
          customer_type: customer.customer_type || 'individual',
          is_active: customer.is_active !== false,
          
          credit_limit: customer.credit_limit?.toString() || '0',
          outstanding_balance: customer.outstanding_balance?.toString() || '0',
          payment_terms: customer.payment_terms?.toString() || '30',
          is_credit_customer: customer.is_credit_customer || parseFloat(customer.credit_limit) > 0
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load customer');
      console.error('Fetch customer error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'credit_limit') {
      const creditValue = parseFloat(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: value,
        is_credit_customer: creditValue > 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      // Validate required fields
      if (!formData.name.trim()) {
        throw new Error('Customer name is required');
      }
      if (!formData.phone.trim()) {
        throw new Error('Phone number is required');
      }
      
      // Prepare customer data (omit empty email — backend rejects invalid email)
      const customerData = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: (formData.email || '').trim() || null,
        company_name: formData.company_name || null,
        address: formData.address || null,
        customer_type: formData.customer_type || 'individual',
        is_active: formData.is_active !== false,
        credit_limit: parseFloat(formData.credit_limit) || 0,
        outstanding_balance: parseFloat(formData.outstanding_balance) || 0,
        payment_terms: parseInt(formData.payment_terms, 10) || 30,
        is_credit_customer: parseFloat(formData.credit_limit) > 0
      };

      const response = await updateCustomer(id, customerData);
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/customers');
        }, 1500);
      } else {
        setError(response.message || 'Failed to update customer');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while updating customer');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/customers');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            sx={{ mr: 2 }}
          >
            Back to Customers
          </Button>
          <Typography variant="h4" sx={{ flexGrow: 1, color: '#2c3e50', fontWeight: 'bold' }}>
            Edit Customer: {formData.name}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Customer updated successfully! Redirecting...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Basic Information" />
              <Tab 
                label={(
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <CreditCardIcon fontSize="small" />
                    <span>Credit Details</span>
                  </Box>
                )} 
              />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Code"
                  name="customer_code"
                  value={formData.customer_code}
                  InputProps={{ readOnly: true }}
                  margin="normal"
                  variant="outlined"
                  helperText="Customer code cannot be changed"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Customer Type *</InputLabel>
                  <Select
                    name="customer_type"
                    value={formData.customer_type}
                    onChange={handleChange}
                    label="Customer Type *"
                  >
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="corporate">Corporate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  disabled={formData.customer_type === 'individual'}
                  helperText={formData.customer_type === 'individual' ? "Not applicable for individuals" : ""}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone *"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {formData.is_active ? "Customer Active" : "Customer Inactive"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.is_active ? 
                          "Customer can make purchases" : 
                          "Customer cannot make purchases"}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 2, bgcolor: '#f8f9fa' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CreditCardIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Credit Account Information
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Update credit limit, outstanding balance, and payment terms
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Credit Limit (LKR) *"
                  name="credit_limit"
                  type="number"
                  value={formData.credit_limit}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{ 
                    inputProps: { min: 0, step: 1000 },
                    startAdornment: 'LKR '
                  }}
                  helperText="Set credit limit for this customer"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Outstanding Balance (LKR) *"
                  name="outstanding_balance"
                  type="number"
                  value={formData.outstanding_balance}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{ 
                    inputProps: { min: 0, step: 100 },
                    startAdornment: 'LKR '
                  }}
                  helperText="Current outstanding amount"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Terms (days) *"
                  name="payment_terms"
                  type="number"
                  value={formData.payment_terms}
                  onChange={handleChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{ inputProps: { min: 0, max: 90 } }}
                  helperText="Default payment period"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Credit Status</InputLabel>
                  <Select
                    name="credit_status"
                    value={formData.is_credit_customer ? "active" : "inactive"}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        is_credit_customer: e.target.value === "active"
                      }));
                    }}
                    label="Credit Status"
                  >
                    <MenuItem value="inactive">Inactive (Cash Only)</MenuItem>
                    <MenuItem value="active">Active (Credit Enabled)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Alert 
                  severity={parseFloat(formData.credit_limit) > 0 ? "info" : "warning"}
                  sx={{ mt: 2 }}
                >
                  {parseFloat(formData.credit_limit) > 0 ? (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        Credit Account Details
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Credit Limit: LKR {parseFloat(formData.credit_limit).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Outstanding Balance: LKR {parseFloat(formData.outstanding_balance).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Available Credit: LKR {(parseFloat(formData.credit_limit) - parseFloat(formData.outstanding_balance)).toLocaleString()}
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Payment Terms: {formData.payment_terms} days
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        Cash-Only Customer
                      </Typography>
                      <Typography variant="caption">
                        This customer cannot make credit purchases. Set credit limit above 0 to enable credit features.
                      </Typography>
                    </>
                  )}
                </Alert>
              </Grid>
            </Grid>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
              size="large"
            >
              Cancel
            </Button>
            
            {activeTab === 0 && (
              <Button
                variant="outlined"
                onClick={() => setActiveTab(1)}
                disabled={saving}
                size="large"
              >
                Next: Credit Details
              </Button>
            )}
            
            {activeTab === 1 && (
              <Button
                variant="outlined"
                onClick={() => setActiveTab(0)}
                disabled={saving}
                size="large"
              >
                Back to Basic Info
              </Button>
            )}
            
            <Button
              variant="contained"
              type="submit"
              startIcon={<SaveIcon />}
              disabled={saving}
              sx={{
                backgroundColor: '#3498db',
                '&:hover': { backgroundColor: '#2980b9' },
                minWidth: 200
              }}
              size="large"
            >
              {saving ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Update Customer'
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default EditCustomer;